import {
    Component,
    Input,
    OnInit,
    OnChanges,
    OnDestroy,
    SimpleChanges,
} from '@angular/core';
import {
    RangeCustom,
    ExceptionData,
    AgeGenderData,
    CALENDAR_SELECT,
    LocalBusinesses,
} from '@models';
import { DateProvider } from '@providers/date.provider';
import { CountingService } from '@app/services';
import { CountingCircleStateService } from '@app/services/counting-circle-state.service';
import { Subscription } from 'rxjs';

@Component({
    standalone: false,
    selector: 'app-age-gender-recognition',
    templateUrl: './age-gender-recognition.component.html',
})
export class AgeGenderRecognitionComponent
    implements OnInit, OnChanges, OnDestroy
{
    @Input() calendarSelect!: CALENDAR_SELECT;
    @Input() range!: RangeCustom;
    @Input() localBusiness!: LocalBusinesses;

    public $data!: AgeGenderData;
    public loading = false;

    private dataSub?: Subscription;

    constructor(
        private service: CountingService,
        private dateProvider: DateProvider,
        private circleState: CountingCircleStateService
    ) {}

    ngOnInit() {
        this.getData();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (
            changes['calendarSelect'] ||
            changes['range'] ||
            changes['localBusiness']
        ) {
            this.circleState.setLoading();
            this.getData();
        }
    }

    public getData() {
        this.loading = true;
        this.dataSub?.unsubscribe();

        let beginHour = this.dateProvider.getOpeningTime(
            this.range.begin,
            this.localBusiness.openingCalendar
        );
        let beginHourVs = this.dateProvider.getOpeningTime(
            this.range.beginVs,
            this.localBusiness.openingCalendar
        );

        let endHour = this.dateProvider.getClosingTime(
            this.range.end,
            this.localBusiness.openingCalendar
        );
        let endHourVs = this.dateProvider.getClosingTime(
            this.range.endVs,
            this.localBusiness.openingCalendar
        );

        let begin = this.dateProvider.setHourInTimeStamp(
            this.range.begin,
            beginHour
        );
        let beginVs = this.dateProvider.setHourInTimeStamp(
            this.range.beginVs,
            beginHourVs
        );
        let end = this.dateProvider.setHourInTimeStamp(this.range.end, endHour);
        let endVs = this.dateProvider.setHourInTimeStamp(
            this.range.endVs,
            endHourVs
        );

        this.dataSub = this.service
            .getAgeGenderData(this.localBusiness.id!, this.calendarSelect, {
                begin,
                end,
                beginVs,
                endVs,
            })
            .subscribe({
                next: (data: AgeGenderData) => {
                    this.$data = data;
                    this.loading = false;

                    this.emitCircleData(data);
                },
                error: () => {
                    this.loading = false;
                    this.emitEmptyCircleData();
                },
            });
    }

    private emitCircleData(data: AgeGenderData): void {
        // Magasin fermé sur la période : on remet le cercle à zéro
        if (this.checkIfClosed(data)) {
            this.emitEmptyCircleData();
            return;
        }

        // On ne bloque que sur une vraie exception API, pas sur un nbPerson à 0
        if (Object.keys(data).includes('exception')) {
            this.emitEmptyCircleData();
            return;
        }
        if (!this.isAgeGender(data)) {
            this.emitEmptyCircleData();
            return;
        }

        this.circleState.emit({
            current: data.current.nbPerson ?? 0,
            versus: data.versus.nbPerson ?? 0,
            variation: (data as any).comparison?.nbPerson ?? 0,
            icon: '/assets/svg/customers-dark.svg',
        });
    }

    private emitEmptyCircleData(): void {
        this.circleState.emit({
            current: 0,
            versus: 0,
            variation: 0,
            icon: '/assets/svg/customers-dark.svg',
        });
    }

    public isAgeGender(x: any): x is AgeGenderData {
        return (
            Object.keys(x).includes('current') &&
            Object.keys(x).includes('versus')
        );
    }

    public checkIfClosed(data: any): boolean {
        return Object.keys(data).includes('closed') && data.closed === true;
    }

    public checkIfnoDataCurrent(data: any): boolean {
        return (
            Object.keys(data).includes('closedBegin') &&
            data.closedBegin === true &&
            data.closed !== true
        );
    }

    public checkIfException(data: AgeGenderData | ExceptionData) {
        if (Object.keys(data).includes('exception')) return true;

        if (this.isAgeGender(data)) {
            // On n'affiche le "noDataToShow" que si les repartitions age/genre
            // sont vides : le versus (ou le nbPerson) a 0 reste une donnee valide.
            return this.isEmptyDistribution(data);
        }

        return false;
    }

    private isEmptyDistribution(data: AgeGenderData): boolean {
        const values = [
            data.current?.gender?.men,
            data.current?.gender?.women,
            data.current?.age?.adult,
            data.current?.age?.child,
        ];

        return values.every((value) => Number(value ?? 0) === 0);
    }

    public checkIfSubscribe() {
        return this.localBusiness.ageGenderEnabled ? true : false;
    }

    ngOnDestroy(): void {
        this.dataSub?.unsubscribe();
    }
}
